import { init as initFullStory, FullStory } from '@fullstory/browser';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { API } from '../../api';
import { ElementAction } from '../../elements/elementAction';
import { ContentPlanRootState as S } from '../../elements/contentPlan/store';
import { Functions } from '../../types/types';
import { View, Workspace } from '../../types';
import { ACState } from '../AccessControl/accessControlReducer';
import { FSEvent } from '../../enums/FSEvent';
import { NodeType } from '../../enums/NodeType';
import { ViewCategory } from '../../enums/viewCategory';
import { TriggerLocation } from '../../enums/TriggerLocation';

enum Role {
  Workspace_User = `Workspace User`
}

export interface PageProperties {
  url?: string;
  workspaceName?: string;
  workspaceRefId?: string;
  viewName?: string;
  viewRefId?: string;
  workspaceRoles?: string[];
}

interface CreateItemEventProperties extends PageProperties {
  triggerLocation: TriggerLocation;
}

interface UpdateItemStatusEventProperties extends PageProperties {
  fromStatusId: number;
  toStatusId: number;
  fromStatusText: string;
  toStatusText: string;
}

interface CommentEventProperties extends PageProperties {
  workflowPhase: string;
}

interface UNIProperties extends PageProperties {
  parentStatusId: number;
  parentStatusText: string;
  triggerLocation?: string;
}

interface InThePlanProperties extends PageProperties {
  from: boolean;
  to: boolean;
  when: string;
  triggerLocation: string;
}

interface ExportProperties {
  viewType?: string;
}

export interface MakePrimaryEventProperties extends PageProperties {
  fieldKey: string;
  hasMultipleValues: boolean;
}

type EventProperties =
  | CreateItemEventProperties
  | UpdateItemStatusEventProperties
  | CommentEventProperties
  | PageProperties
  | UNIProperties
  | InThePlanProperties
  | ExportProperties
  | MakePrimaryEventProperties;

interface EventRequest {
  name: string;
  properties: EventProperties;
}

export class FullStoryManager {
  private static _instance: FullStoryManager;

  private constructor() {}

  public static getInstance() {
    return this._instance || (this._instance = new FullStoryManager());
  }

  public initialize(): ElementAction<Functions, S> {
    return async (dispatch, getState, { getAPI }): Promise<void> => {
      try {
        initFullStory(
          { orgId: process.env.FULL_STORY_ID },
          dispatch(this.setIdentity())
        );
      } catch (e) {
        console.error(`Failed to initialize Fullstory with error`, e);
      }
      return;
    };
  }
  public setIdentity(): ElementAction<Functions, S> {
    return async (dispatch, getState, { getAPI }): Promise<void> => {
      try {
        const api = getAPI();
        const userInfo = await api.getUserInfo();
        const jobRoles = !!userInfo?.props?.jobRoles?.length
          ? userInfo?.props?.jobRoles
          : [];
        const practice = !!userInfo?.props?.researchPractice
          ? userInfo?.props?.researchPractice
          : 'N/A';
        const businessUnit = !!userInfo?.props?.empHdcLevel1
          ? userInfo?.props?.empHdcLevel1
          : 'N/A';
        const uid = btoa(userInfo.props.empCd.toString());
        FullStory('setIdentity', {
          uid,
          properties: {
            businessUnit,
            jobRoles,
            practice
          }
        });
      } catch (e) {
        console.error(`Failed to set Fullstory identity with error`, e);
      }
    };
  }

  public setPageProperties(view: View.Setting): ElementAction<Functions, S> {
    return async (dispatch, getState): Promise<void> => {
      try {
        const { planning, accessControl } = getState();

        const {
          url,
          workspaceName,
          workspaceRefId,
          viewName,
          viewRefId,
          workspaceRoles
        } = getFullStoryProperties(
          view,
          planning?.selectedWorkspace,
          accessControl
        );
        const properties = {
          url,
          workspaceName,
          workspaceRefId,
          viewName,
          viewRefId,
          workspaceRoles
        };
        FullStory('setProperties', {
          type: 'page',
          properties
        });
      } catch (e) {
        console.error(`Failed to set Fullstory properties with error`, e);
      }
    };
  }

  public captureEvent(
    name: FSEvent,
    additionalProperties?: EventProperties
  ): ElementAction<Functions, S> {
    return async (dispatch, getState): Promise<void> => {
      try {
        const { view: viewState, planning, accessControl } = getState();
        const { selectedWorkspace } = planning;
        const view = viewState?.selected;
        const fsProperties = getFullStoryProperties(
          view,
          selectedWorkspace,
          accessControl,
          name == FSEvent.Search ||
            name == FSEvent.LightUpdate ||
            name == FSEvent.AddedOrRemovedFromThePlan ||
            name == FSEvent.EditModeEntered ||
            name == FSEvent.ViewContentMetrics
            ? ['workspaceName', 'workspaceRefId', 'workspaceRoles']
            : [
                'workspaceName',
                'workspaceRefId',
                'workspaceRoles',
                'viewName',
                'viewRefId'
              ]
        );
        const eventRequest: EventRequest = {
          name,
          properties: {
            ...fsProperties,
            ...(additionalProperties || {})
          }
        };
        FullStory('trackEvent', eventRequest);
      } catch (e) {
        console.error(`Failed to capture Fullstory event with error`, e);
      }
    };
  }
}

export const useFullStory = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    getInstance: () => FullStoryManager.getInstance(),
    initialize: () => dispatch(FullStoryManager.getInstance().initialize()),
    setIdentity: () => dispatch(FullStoryManager.getInstance().setIdentity()),
    setPageProperties: (view: View.Setting) =>
      dispatch(FullStoryManager.getInstance().setPageProperties(view)),
    captureEvent: (
      eventName: FSEvent,
      additionalProperties?: EventProperties
    ) =>
      dispatch(
        FullStoryManager.getInstance().captureEvent(
          eventName,
          additionalProperties
        )
      )
  };
};

const getFullStoryProperties = (
  view: View.Setting,
  selectedWorkspace: Workspace,
  accessControl: ACState,
  propsKeys?: (keyof PageProperties)[]
): PageProperties => {
  const viewRefId = view?.props?.refId;
  const viewName = view?.props?.name;
  const workspaceRefId = selectedWorkspace?.props?.refId;
  const workspaceName = selectedWorkspace?.props?.name;
  const workspaceRoles = !!accessControl?.permissions[workspaceRefId]?.roles
    ?.length
    ? accessControl?.permissions[workspaceRefId]?.roles
    : [Role.Workspace_User];

  let selectedFolder = '';
  selectedWorkspace?.folders?.forEach(f => {
    f?.views?.forEach(v => {
      if (v?.props?.refId == viewRefId) {
        selectedFolder = f?.props?.name;
      }
    });
  });
  const formattedViewName = `${
    !view?.isMyItemsView &&
    view?.category != ViewCategory.EnterpriseData &&
    !(
      view?.category == ViewCategory.CustomData &&
      view?.dataSchema?.props?.type == NodeType.Item
    )
      ? `${selectedWorkspace?.props?.prefix} / `
      : ``
  }${
    !!selectedFolder
      ? `${selectedFolder} / `
      : view?.category == ViewCategory.CustomData ||
          view?.category == ViewCategory.EnterpriseData
        ? `${view.category} / `
        : ``
  }${viewName}`;
  const allProps = {
    url: window.location.href,
    workspaceName,
    workspaceRefId,
    viewName: formattedViewName,
    viewRefId,
    workspaceRoles
  };
  if (!propsKeys) return allProps;
  return Object.entries(allProps).reduce((props, [key, val]) => {
    if (propsKeys.includes(key as any)) props[key] = val;
    return props;
  }, {});
};
